import React, { useState } from "react";
import { Modal } from "../../components/Modal";
import { Player } from "../../sim/entities/Player";
import { serializeGame } from "../../sim/save/SaveFile";
import { readEngineSave } from "../saveSlot";
import { sendFeedback } from "../telemetry/telemetry";
import { useGame } from "../useShell";

/**
 * The feedback button and the card it opens: a note from the player,
 * sent to the developer with a copy of their shop attached (issue
 * #248). The save is a full snapshot, so the moment someone got stuck
 * in can be opened and looked at.
 *
 * It sits on the start menu and in the pause menu. Where a shop is open
 * the attached save is the world as it stands right now, snapshotted
 * the way the autosave does it; on the start menu it's whatever the
 * slot holds, or nothing.
 */
export const FeedbackAction: React.FC<{
  /** "chrome" sits on the dark workshop background, "paper" on a paper card. */
  variant?: "chrome" | "paper";
  className?: string;
}> = ({ variant = "chrome", className = "" }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        className={`${variant === "paper" ? "button-paper" : "button-ghost"} ${className}`}
        onClick={() => setOpen(true)}
        data-testid="feedback-button"
      >
        Send Feedback
      </button>
      {open && <FeedbackCard onClose={() => setOpen(false)} />}
    </>
  );
};

type Stage = "writing" | "sending" | "sent" | "failed";

const FeedbackCard: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const game = useGame();
  const [note, setNote] = useState("");
  const [stage, setStage] = useState<Stage>("writing");

  const send = async () => {
    const trimmed = note.trim();
    if (trimmed === "") return;
    setStage("sending");
    const arrived = await sendFeedback(trimmed, currentSave());
    setStage(arrived ? "sent" : "failed");
  };

  /** The shop as it stands, or the stored save when no shop is open. */
  const currentSave = (): unknown | null =>
    game.entities.tryGetSingleton(Player)
      ? serializeGame(game)
      : readEngineSave();

  return (
    <Modal
      onClose={onClose}
      label="Send feedback"
      panelClassName="paper-card w-full max-w-sm flex flex-col gap-4"
      backdropCloses={stage !== "sending"}
    >
      <h2 className="font-condensed font-bold text-2xl uppercase tracking-wide text-ink-black">
        Send Feedback
      </h2>
      {stage === "sent" ? (
        <>
          <p className="font-typewriter text-sm text-ink-black">
            Sent. Thanks for the note.
          </p>
          <div className="flex justify-end border-t-2 border-ink-black/20 pt-4">
            <button className="button-paper" onClick={onClose} autoFocus>
              Done
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="font-typewriter text-sm text-ink-black">
            Write what happened, or what you wish had happened. The note goes to
            the developer along with a copy of your shop as it stands, so the
            same moment can be opened and looked at.
          </p>
          <textarea
            className="font-typewriter text-sm text-ink-black bg-paper-ivory border border-ink-black/30 rounded p-2 min-h-28 resize-y focus:outline-none focus:border-ink-black"
            placeholder="What happened?"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={stage === "sending"}
            autoFocus
            data-testid="feedback-note"
          />
          {stage === "failed" && (
            <p className="font-typewriter text-xs text-ink-fade">
              The note could not be sent. Try again in a moment.
            </p>
          )}
          <div className="flex items-center justify-between gap-3 border-t-2 border-ink-black/20 pt-4">
            <button
              className="button-paper"
              onClick={onClose}
              disabled={stage === "sending"}
              data-sfx="ui-back"
            >
              Never Mind
            </button>
            <button
              className="button-paper"
              onClick={send}
              disabled={stage === "sending" || note.trim() === ""}
              data-testid="feedback-send"
            >
              {stage === "sending" ? "Sending…" : "Send"}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
};
